import FaceLove from 'assets/svg/icons/FaceLove';
import FaceSad from 'assets/svg/icons/FaceSad';
import FaceStraight from 'assets/svg/icons/FaceStraight';
import Button from 'components/atoms/Button/Button.component';
import { getIntercom } from 'hooks/intercom.hook';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { logAmplitude } from 'utils/amplitude';
import { WEB_YEARLYREVIEW_CSATCLICK } from 'utils/amplitude/events/ai.amplitude';
import { getReviewYears } from '../helpers';

const FooterText: FC = () => {
  const { year, newYear } = getReviewYears();
  const { t } = useTranslation();

  const [csatValue, setCsatValue] = useState<number | null>(null);

  const handleCsatClick = (value: number) => {
    logAmplitude(WEB_YEARLYREVIEW_CSATCLICK, { value: value.toString() });
    setCsatValue(value);
  };

  const { showIntercom } = getIntercom();

  return (
    <div className="relative overflow-hidden bg-white flex flex-col items-center justify-center">
      <svg
        width="968"
        height="480"
        viewBox="0 0 968 480"
        className="absolute left-1/2 -translate-x-1/2 mix-blend-hard-light top-[-88px]"
      >
        <g
          style={{
            filter: 'url(#gradient_footer)',
            opacity: '0.35',
          }}
        >
          <circle opacity="0.65" cx="644" cy="280" r="80" fill="#5AB7F9" />
          <circle opacity="0.65" cx="324" cy="280" r="80" fill="#5AB7F9" />
          <circle cx="484" cy="240" r="120" fill="#8059C8" />
        </g>
        <defs>
          <filter
            id="gradient_footer"
            x="124"
            y="0"
            width="720"
            height="480"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur stdDeviation="60" />
          </filter>
        </defs>
      </svg>
      <ReactMarkdown
        className="max-w-[800px] mx-auto text-center relative z-10 pb-[40px] pt-[88px] leading-7"
        components={{
          p: ({ children }) => (
            <p className="font-medium text-primary text-md">{children}</p>
          ),
        }}
      >
        {t('yearly_review.footer_text_2024', { year, newYear })}
      </ReactMarkdown>

      {csatValue === null ? (
        <div className="flex flex-col items-center justify-center pb-5 gap-2 z-10 text-primary-700">
          <p className="text-md font-medium">
            {t('yearly_review.footer.csat_title')}
          </p>
          <div className="flex gap-3 text-4xl z-10">
            <Button
              className="hover:scale-110 duration-100 hover:text-red-400 transition-all"
              asDefault
              onClick={() => handleCsatClick(-1)}
            >
              <FaceSad className="h-12 w-12" />
            </Button>
            <Button
              className="hover:scale-110 duration-100 hover:text-primary transition-all"
              asDefault
              onClick={() => handleCsatClick(0)}
            >
              <FaceStraight className="h-12 w-12" />
            </Button>
            <Button
              className="hover:scale-110 duration-100 hover:text-green-400 transition-all"
              asDefault
              onClick={() => handleCsatClick(1)}
            >
              <FaceLove className="h-12 w-12" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center pb-5 gap-2 z-10 text-primary-700">
          <p className="text-md font-medium">
            {csatValue === 1
              ? t('yearly_review.footer.csat_thanks_positive')
              : t('yearly_review.footer.csat_thanks_negative')}
          </p>

          {csatValue !== 1 && (
            <Button structure="text" onClick={showIntercom}>
              {t('talk_to_an_expert')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default FooterText;
